import { DatePicker } from '@/components/DatePicker';
import { useSetEnrolmentDates } from '@/hooks/useSetEnrolmentDates';
import { useLocale } from '@/providers/LocaleProvider';
import { useSnackbar } from '@/providers/SnackbarProvider';
import { KLAYO_COLORS } from '@/themes';
import { Alert, Stack, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { Dialog } from './Dialog';

export type SetEnrolmentDateMode = 'dueDate' | 'expiry';

interface SetEnrolmentDateDialogProps {
  open: boolean;
  mode: SetEnrolmentDateMode;
  studentName?: string;
  courseName?: string;
  /** Targets the learner's standing enrolment — the server flag canSetDates guarantees it is set. */
  courseEnrollmentId?: string;
  /** Both of the enrolment's current dates; the dialog picks the one for its mode, so the
   *  mode-to-field mapping lives here only, next to the write-path mapping in onSave. */
  dueDate?: string;
  expiryDate?: string;
  theme?: any;
  onClose: () => void;
  /** Fired after the server accepted the write — hosts refetch their lists here. */
  onSuccess: () => void;
}

export const SetEnrolmentDateDialog = ({
  open,
  mode,
  studentName,
  courseName,
  courseEnrollmentId,
  dueDate,
  expiryDate,
  theme,
  onClose,
  onSuccess,
}: SetEnrolmentDateDialogProps) => {
  const { translate } = useLocale();
  const { showSnackbar } = useSnackbar();
  const { mutate: setDates, isPending } = useSetEnrolmentDates();

  const [date, setDate] = useState<Date | null>(null);
  const [dateValid, setDateValid] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDue = mode === 'dueDate';
  const currentDate = isDue ? dueDate : expiryDate;

  useEffect(() => {
    if (!open) return;
    const initial = currentDate ? new Date(currentDate) : null;
    setDate(initial);
    setDateValid(!!initial);
    setError(null);
  }, [open, mode, currentDate]);

  // Same calendar day as the value the dialog opened with — Save would be a no-op rewrite of the
  // date LearnUpon already holds. Compared as formatted days: two Date objects for one day differ
  // by time-of-day, and format() is only safe once dateValid says the input parses.
  const isUnchanged =
    !!date &&
    dateValid &&
    !!currentDate &&
    format(date, 'yyyy-MM-dd') === format(new Date(currentDate), 'yyyy-MM-dd');

  // While the PUT is in flight, ignore every close path (Escape, the X button, Cancel): closing
  // re-arms the menu for another row, and AxiosClient keys in-flight requests by method+url only,
  // so a second row's save would silently abort this one — a lost write with no feedback.
  const handleClose = () => {
    if (!isPending) onClose();
  };
  const title = translate(
    isDue ? 'manage.setEnrolmentDate.setDueDate' : 'manage.setEnrolmentDate.setExpiryDate'
  );

  const onSave = () => {
    if (!courseEnrollmentId || !date || !dateValid) return;
    setError(null);
    // Bare calendar date from local parts (same contract as periodFilter's toApiDate) — never
    // toISOString(), which can shift the picked day across a timezone boundary.
    const value = format(date, 'yyyy-MM-dd');
    setDates(
      // Only the edited date; the omitted one is left unchanged by the server.
      { courseEnrollmentId, ...(isDue ? { dueDate: value } : { expiryDate: value }) },
      {
        onSuccess: () => {
          showSnackbar(
            translate(
              isDue ? 'manage.setEnrolmentDate.dueUpdated' : 'manage.setEnrolmentDate.expiryUpdated'
            ),
            'success'
          );
          onSuccess();
          onClose();
        },
        onError: (err: any) => {
          // Keep the dialog open so the manager can correct the date (e.g. due after expiry).
          // The API error body's field is errorMessage (ApiError), not message.
          setError(
            err?.response?.data?.errorMessage || translate('manage.setEnrolmentDate.updateFailed')
          );
        },
      }
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      theme={theme}
      fullWidth
      maxWidth='sm'
      title={title}
      closeButtonSx={{ display: 'block' }}
      // ActionsContainer lays actions out row-reverse on desktop: first action lands right-most.
      actions={[
        {
          label: translate('common.save'),
          variant: 'filled',
          onClick: onSave,
          disabled: !date || !dateValid || isUnchanged || isPending,
          isLoading: isPending,
        },
        {
          label: translate('common.cancel'),
          variant: 'outlined',
          onClick: handleClose,
          disabled: isPending,
        },
      ]}
    >
      <Stack gap={3}>
        {error && <Alert severity='error'>{error}</Alert>}
        <Typography variant='body1' color={KLAYO_COLORS.GreyGR900}>
          <Trans
            i18nKey={
              isDue ? 'manage.setEnrolmentDate.dueBody' : 'manage.setEnrolmentDate.expiryBody'
            }
            values={{ student: studentName ?? '', course: courseName ?? '' }}
            components={{ b: <strong /> }}
          />
        </Typography>
        {/* allowEmpty so an empty picker is not flagged "Invalid date" the moment the dialog
            opens with no current value — Save stays disabled until a date is picked anyway. */}
        <DatePicker
          theme={theme}
          label={null}
          value={date}
          allowEmpty={true}
          sx={{ width: '100%' }}
          disabled={isPending}
          onChange={(value?: Date | null, valid?: boolean) => {
            setDate(value ?? null);
            setDateValid(!!valid && !!value);
          }}
        />
      </Stack>
    </Dialog>
  );
};
